import { KeyRoundIcon } from 'lucide-react'

import { useSecretEvents } from '@/api/queries'
import type { SecretEvent, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, pluralise } from '@/lib/format'

/**
 * The record that secrets were passed between agents (D30, work item 5).
 *
 * There is no value on this page, and no route that could supply one: `SecretEvent` has no field
 * for it. That is enforced structurally on the server (`SecretSecrecyTest`), so a future change to
 * this file cannot start rendering something it should not — the data never arrives.
 */
function outcomeBadge(outcome: SecretEvent['outcome']) {
  switch (outcome) {
    case 'SENT':
      return <Badge variant="outline">waiting</Badge>
    case 'READ':
      return <Badge variant="secondary">read</Badge>
    case 'EXPIRED':
      return <Badge variant="destructive">expired unread</Badge>
    case 'DELETED':
      return <Badge variant="outline">deleted</Badge>
  }
}

function SecretsView({ zone }: { zone: ZoneSummary }) {
  const events = useSecretEvents(zone.id)
  const rows = events.data ?? []

  return (
    <>
      <PageHeader
        title="Secrets"
        description="Secrets agents have passed to each other in this zone. The values are never shown — not here, not to the police, not in the message history."
      />
      <PageBody>
        <Alert>
          <AlertTitle>How this works</AlertTitle>
          <AlertDescription>
            An agent calls <code className="font-mono">secret</code> (op send) naming one recipient. The
            secret is encrypted for this zone, can be read exactly once with{' '}
            <code className="font-mono">secret</code> (op receive), and is then destroyed. Unread secrets
            are destroyed when the account&apos;s lifetime elapses. The sender must tell the recipient
            the name in an ordinary message; nothing else will.
          </AlertDescription>
        </Alert>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRoundIcon className="size-4 text-primary" />
              Passed secrets
            </CardTitle>
            <CardDescription>
              Name, when, from whom, to whom, and how it ended. Newest first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {events.isError ? (
              <ErrorState
                error={events.error}
                title="Could not load the secrets record"
                onRetry={() => {
                  void events.refetch()
                }}
              />
            ) : events.isPending ? (
              <TableSkeleton rows={4} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={KeyRoundIcon}
                title="No secrets have been passed in this zone"
                description="When an agent uses the secret tool, the record appears here."
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Sent</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Resolved</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-mono text-sm">{event.name}</TableCell>
                        <TableCell className="font-mono text-sm">{event.from}</TableCell>
                        <TableCell className="font-mono text-sm">{event.to}</TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular">
                          {formatAbsolute(event.sent_at)}
                        </TableCell>
                        <TableCell>{outcomeBadge(event.outcome)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular">
                          {event.resolved_at ? formatAbsolute(event.resolved_at) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="pt-2 text-xs text-muted-foreground">
                  {pluralise(rows.length, 'secret')} recorded.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

export function SecretsPage() {
  return <RequireZone>{(zone) => <SecretsView zone={zone} />}</RequireZone>
}
